import type { EyeStyleId, EyeStyleOptions } from '../../AppearanceDefinitions';
import type { HumanoidPose } from '../AttachmentGeometry';
import { matchedSockets } from './MatchedEyes';

/**
 * Plan 12 eye-style library: a range of eyes to choose per character (DJ:
 * "not one style for everyone"). Every style draws two MATCHING eyes from one
 * socket pair, so size, spacing and height knobs apply to all of them; facing
 * is carried by a slight forward gaze, the nose and the props. `profile` (the
 * older near-big/far-small eye) is an opt-in handled by FaceParts.
 *
 * Coordinates are the right-facing face space used by FaceParts (the caller
 * keeps the face-rig offset and the whole-sprite mirror).
 */
export const EYE_STYLES: readonly EyeStyleId[] = [
  'matched', 'oval', 'dot', 'button', 'wide', 'sleepy', 'happy', 'stern', 'glint', 'closed', 'sparkle', 'lashes', 'profile',
];

/** One-line description per style, used by the choice sheets. */
export const EYE_STYLE_NOTES: Record<EyeStyleId, string> = {
  matched: 'player default: the eyes expression, two equal eyes',
  oval: 'cute ghost oval, two catch-lights',
  dot: 'small dot eyes',
  button: 'round button eyes, big shine',
  wide: 'white eyes with pupils',
  sleepy: 'half-lidded',
  happy: 'happy arcs ^^',
  stern: 'narrow, flat brows',
  glint: 'dark oval with a star glint',
  closed: 'calm, eyes closed',
  sparkle: 'star eyes',
  lashes: 'oval with lashes',
  profile: 'near eye bigger (opt-in facing cue)',
};

const PUPIL = '#26313e';

interface Socket { x: number; y: number; rx: number; ry: number }

const round2 = (v: number) => Number(v.toFixed(2));
const clamp = (v: number | undefined, lo: number, hi: number, fallback: number) =>
  (Number.isFinite(v) ? Math.min(hi, Math.max(lo, v as number)) : fallback);

/** The matching socket pair for a set of knobs. */
export function eyeSockets(options: Omit<EyeStyleOptions, 'style'> = {}): { far: Socket; near: Socket } {
  const k = clamp(options.scale, 0.7, 1.4, 1);
  const spread = clamp(options.spacing, -6, 6, 0) / 2;
  const y = 43 + clamp(options.height, -5, 5, 0);
  const rx = round2(5.8 * k);
  const ry = round2(6.6 * k);
  return {
    far: { x: round2(53 - spread), y, rx, ry },
    near: { x: round2(76 + spread), y, rx, ry },
  };
}

/** Socket pair for any library style (glasses fit around these). */
export function socketsFor(options: EyeStyleOptions): { far: Socket; near: Socket } {
  return options.style === 'matched' ? matchedSockets(options) : eyeSockets(options);
}

type Side = 'far' | 'near';

function solid(side: Side, e: Socket, rxK: number, ryK: number, glints: 'two' | 'big' | 'tiny' | 'star'): string {
  const rx = round2(e.rx * rxK);
  const ry = round2(e.ry * ryK);
  let shine = '';
  if (glints === 'two' || glints === 'big') {
    const r = round2(e.rx * (glints === 'big' ? 0.46 : 0.36));
    shine = `<circle cx="${round2(e.x + rx * 0.32)}" cy="${round2(e.y - ry * 0.36)}" r="${r}" fill="#ffffff" />`
      + `<circle cx="${round2(e.x - rx * 0.35)}" cy="${round2(e.y + ry * 0.36)}" r="${round2(r * 0.45)}" fill="#ffffff" opacity="0.8" />`;
  } else if (glints === 'tiny') {
    shine = `<circle cx="${round2(e.x + rx * 0.3)}" cy="${round2(e.y - ry * 0.3)}" r="${round2(Math.max(0.6, rx * 0.3))}" fill="#ffffff" />`;
  } else {
    const s = e.rx * 0.5;
    const cx = e.x + rx * 0.25;
    const cy = e.y - ry * 0.3;
    shine = `<path d="M${round2(cx)},${round2(cy - s)} L${round2(cx + s * 0.25)},${round2(cy - s * 0.25)} L${round2(cx + s)},${round2(cy)} L${round2(cx + s * 0.25)},${round2(cy + s * 0.25)} L${round2(cx)},${round2(cy + s)} L${round2(cx - s * 0.25)},${round2(cy + s * 0.25)} L${round2(cx - s)},${round2(cy)} L${round2(cx - s * 0.25)},${round2(cy - s * 0.25)} Z" fill="#ffffff" />`;
  }
  return `<g data-face-eye="${side}"><ellipse data-face-socket="${side}" cx="${e.x}" cy="${e.y}" rx="${rx}" ry="${ry}" fill="${PUPIL}" />${shine}</g>`;
}

function wide(side: Side, e: Socket, ink: string, gazeX: number): string {
  const r = round2(e.rx * 0.5);
  const px = round2(e.x + e.rx * 0.22 + gazeX);
  return `<g data-face-eye="${side}">
    <ellipse data-face-socket="${side}" cx="${e.x}" cy="${e.y}" rx="${e.rx}" ry="${e.ry}" fill="#fff9eb" stroke="${ink}" stroke-width="1.6" />
    <circle data-face-pupil="${side}" cx="${px}" cy="${e.y}" r="${r}" fill="${PUPIL}" />
    <circle cx="${round2(px + 0.7)}" cy="${round2(e.y - 0.9)}" r="${round2(e.rx * 0.14)}" fill="#ffffff" />
  </g>`;
}

const arc = {
  closedDown: (e: Socket) => `M${round2(e.x - e.rx - 1)},${e.y} Q${e.x},${round2(e.y + 5)} ${round2(e.x + e.rx + 1)},${e.y}`,
  happy: (e: Socket) => `M${round2(e.x - e.rx - 1)},${round2(e.y + 1)} Q${e.x},${round2(e.y - 9)} ${round2(e.x + e.rx + 1)},${round2(e.y + 1)}`,
  calm: (e: Socket) => `M${round2(e.x - e.rx)},${round2(e.y - 0.5)} Q${e.x},${round2(e.y + 3.5)} ${round2(e.x + e.rx)},${round2(e.y - 0.5)}`,
  lid: (e: Socket) => `M${round2(e.x - e.rx - 1)},${round2(e.y - 0.6)} L${round2(e.x + e.rx + 1)},${round2(e.y - 0.6)}`,
  brow: (e: Socket, side: Side, drop: number) => {
    const outer = side === 'far' ? e.x - e.rx - 1.5 : e.x + e.rx + 1.5;
    const inner = side === 'far' ? e.x + e.rx : e.x - e.rx;
    const top = e.y - e.ry * 0.9 - 2;
    return `M${round2(outer)},${round2(top)} L${round2(inner)},${round2(top + drop)}`;
  },
  cross: (e: Socket) => {
    const d = e.rx * 0.8;
    return `M${round2(e.x - d)},${round2(e.y - d)} L${round2(e.x + d)},${round2(e.y + d)} M${round2(e.x + d)},${round2(e.y - d)} L${round2(e.x - d)},${round2(e.y + d)}`;
  },
  hurt: (e: Socket, side: Side) => {
    const a = side === 'near' ? e.x + e.rx : e.x - e.rx;
    const b = side === 'near' ? e.x - e.rx + 1 : e.x + e.rx - 1;
    return `M${round2(a)},${round2(e.y - 5)} L${round2(b)},${e.y} L${round2(a)},${round2(e.y + 4)}`;
  },
  lashes: (e: Socket, side: Side) => {
    const dir = side === 'near' ? 1 : -1;
    const x = e.x + dir * e.rx * 0.75;
    const y = e.y - e.ry * 0.7;
    return `M${round2(x)},${round2(y)} l${round2(dir * 3)},-2.6 M${round2(x - dir * 1.8)},${round2(y - 1)} l${round2(dir * 2.2)},-3.2 M${round2(x + dir * 1.4)},${round2(y + 1.6)} l${round2(dir * 3.2)},-1.2`;
  },
  star: (e: Socket) => {
    const pts: string[] = [];
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? e.ry * 1.05 : e.ry * 0.45;
      const a = -Math.PI / 2 + (i * Math.PI) / 5;
      pts.push(`${round2(e.x + r * Math.cos(a))},${round2(e.y + r * Math.sin(a))}`);
    }
    return `M${pts.join(' L')} Z`;
  },
};

/**
 * Two matching eyes in the chosen style. Blink, hurt and death replace every
 * style with shared line art so the role idle's blink keeps working.
 */
export function renderEyeStyle(options: EyeStyleOptions, ink: string, pose: HumanoidPose = 'idle'): string {
  const style = EYE_STYLES.includes(options.style) ? options.style : 'oval';
  const base = eyeSockets(options);
  const gazeX = pose === 'attack' ? 0.8 : pose === 'cast' ? 0.55 : 0.4;
  const shift = (e: Socket): Socket => ({ ...e, x: round2(e.x + gazeX) });
  const far = shift(base.far);
  const near = shift(base.near);
  const stroke = `fill="none" stroke="${ink}" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"`;
  const both = (fn: (e: Socket, side: Side) => string) => `${fn(far, 'far')}${fn(near, 'near')}`;
  const state = pose === 'death' || pose === 'blink' || pose === 'hurt' ? pose : 'open';
  let content: string;
  if (pose === 'death') content = `<path ${stroke} d="${arc.cross(far)} ${arc.cross(near)}" />`;
  else if (pose === 'blink') content = `<path ${stroke} d="${arc.closedDown(far)} ${arc.closedDown(near)}" />`;
  else if (pose === 'hurt') content = `<path ${stroke} d="${arc.hurt(far, 'far')} ${arc.hurt(near, 'near')}" />`;
  else {
    switch (style) {
    case 'dot': content = both((e, s) => solid(s, e, 0.48, 0.48, 'tiny')); break;
    case 'button': content = both((e, s) => solid(s, e, 1.05, 0.95, 'big')); break;
    case 'wide': content = both((e, s) => wide(s, e, ink, gazeX * 0.5)); break;
    case 'sleepy':
      content = both((e, s) => solid(s, { ...e, y: round2(e.y + e.ry * 0.3) }, 1, 0.5, 'tiny'))
        + `<path ${stroke} d="${arc.lid(far)} ${arc.lid(near)}" />`;
      break;
    case 'happy': content = `<path ${stroke} d="${arc.happy(far)} ${arc.happy(near)}" />`; break;
    case 'stern':
      content = both((e, s) => solid(s, e, 1.1, 0.42, 'tiny'))
        + `<path ${stroke} d="${arc.brow(far, 'far', 2.5)} ${arc.brow(near, 'near', 2.5)}" />`;
      break;
    case 'glint': content = both((e, s) => solid(s, e, 1, 1.1, 'star')); break;
    case 'closed': content = `<path ${stroke} d="${arc.calm(far)} ${arc.calm(near)}" />`; break;
    case 'sparkle':
      content = `<g data-face-star-eyes="true" fill="#ffd36a" stroke="${ink}" stroke-width="1.1" stroke-linejoin="round"><path d="${arc.star(far)}" /><path d="${arc.star(near)}" /></g>`;
      break;
    case 'lashes':
      content = both((e, s) => solid(s, e, 0.95, 1.05, 'two'))
        + `<path fill="none" stroke="${ink}" stroke-width="1.6" stroke-linecap="round" d="${arc.lashes(far, 'far')} ${arc.lashes(near, 'near')}" />`;
      break;
    case 'oval':
    default:
      content = both((e, s) => solid(s, e, 1.02, 1.12, 'two'));
      break;
    }
  }
  return `<g data-face-style="${style}" data-face-state="${state}" data-face-gaze="right" data-face-eye-library="${style}">${content}</g>`;
}
